"use client";
import { Image } from "@/components/shared/image";
import { Reveal } from "@/components/ui/animations";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { Shell } from "@/components/ui/shell";
import { Lang } from "@/i18n/settings";
import { AboutCeremonyProps } from "@/services/about/about.server";
import { Categories } from "./categories";
import { useMemo } from "react";

import dynamic from "next/dynamic";

const HtmlContent = dynamic(() => import("@/components/common/html-content"), {
  ssr: false,
});

type Props = {
  lang: Lang;
  data: AboutCeremonyProps[];
};

export function Ceremony({ lang, data }: Props) {
  const categories = useMemo(() => {
    return data?.map((item) => item?.title);
  }, []);

  return (
    <Carousel
      opts={{
        align: "center",
        direction: lang === "ar" ? "rtl" : "ltr",
        loop: true,
      }}
      className="w-full"
    >
      <div className="relative h-full w-full">
        <Categories data={categories} />
        <CarouselContent className="!ml-0 w-full">
          {data.map((item) => (
            <CarouselItem
              key={item.id}
              className="relative mx-auto !ml-0 flex min-h-[80vh] w-full flex-col !pl-0 pt-20 sm:min-h-[60vh] sm:pt-0 lg:min-h-[40vh] lg:pb-0 lg:pt-20 xl:min-h-[80vh] landscape:pb-20 landscape:pt-32 lg:landscape:min-h-[80vh] landscape:xl:min-h-[60vh]"
            >
              <Shell className="z-50 mx-auto h-full max-w-[1000px] select-none flex-col justify-center gap-0 lg:flex">
                <div className="my-auto flex flex-1 flex-col items-center justify-start gap-4 sm:justify-center lg:flex-row lg:justify-between lg:gap-20 lg:text-start">
                  <Reveal className="max-h-[219px] max-w-[330px] flex-shrink-0 lg:max-h-max">
                    <Image
                      src={item?.image_link}
                      loading="eager"
                      priority={true}
                      width={5000}
                      height={5000}
                      alt={item?.title}
                      className="h-full w-full rounded-[40px] object-cover"
                    />
                  </Reveal>

                  <Reveal className="text-secondary lg:text-lg ltr:font-playfair rtl:lg:text-xl">
                    <HtmlContent
                      className="flex flex-col gap-4 sm:gap-6"
                      content={item?.description}
                    />
                  </Reveal>
                </div>
              </Shell>
              <div className="absolute inset-0 z-30 bg-black/30" />
              <div className="absolute inset-0 -z-10 blur-[14px]">
                <Image
                  loading="eager"
                  priority={true}
                  src={item?.image_link}
                  width={1000}
                  height={1000}
                  alt="Image"
                  className="h-full w-full object-cover"
                />
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
      </div>
    </Carousel>
  );
}
